import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import EditSellerContactDetailsComp from "../components/EditSellerContactDetailsComp";
import styles from "./StoreSettingsPage.module.css"
import { useAppContext } from "../context/AppContext";
import EditSellerAddressComp from "../components/EditSellerAddressComp";
import UploadProgressModalComp from "../components/UploadProgressModalComp";
import fileService from "../services/fileService";
import EditStoreProfileComp from "../components/EditStoreProfileComp";
import EditStorePoliciesComp from "../components/EditStorePoliciesComp";
import EditPaymentMethodsComp from "../components/EditPaymentMethodsComp";
import { SellerAccountModel } from "../models/SellerAccountModel";
import ConfirmDialogueComp from "../components/ConfirmDialogueComp";
import AddSellerAccountComp from "../components/AddSellerAccountComp";
import sellerAccountService from "../services/sellerAccountService";

const StoreSettingsPage = () => {

  const sellerImageURL = process.env.REACT_APP_SELLER_IMAGE_URL;
  const resourceURL = process.env.REACT_APP_RESOURCE_URL;

  const { seller, setSeller } = useAppContext();
  const { sellerAccount } = useAppContext();

  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadErrorMessage, setLogoUploadErrorMessage] = useState("");

  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const [addressModalVisible, setAddressModalVisible] = useState(false);
  const [contactDetailsModalVisible, setContactDetailsModalVisible] = useState(false);
  const [paymentMethodsModalVisible, setPaymentMethodsModalVisible] = useState(false);
  const [storePoliciesModalVisible, setStorePoliciesModalVisible] = useState(false);
  const [addSellerAccountModalVisible, setAddSellerAccountModalVisible] = useState(false);

  const [sellerAccounts, setSellerAccounts] = useState<SellerAccountModel[]>([]);

  const [deleteSellerAccountId, setDeleteSellerAccountId] = useState<number | null>(null);
  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const newLogoFile = useRef<File | null>(null);

  const getSellerAccounts = async () => {
    try {
      const accounts = await sellerAccountService.getSellerAccounts();
      setSellerAccounts(accounts);
    } catch (err) {
      console.error("Failed to get seller accounts", err);
    }
  };

  useEffect(() => {
    getSellerAccounts();
  }, []);

  const addStaffAccount = () => {
    setAddSellerAccountModalVisible(true);
  };

  const deleteStaffAccount = (sellerAccountId: number) => {
    if (sellerAccountId) {
      setDeleteSellerAccountId(sellerAccountId);
      setConfirmDeleteModalVisible(true);
    }
  }

  const handleDelete = async () => {
    const deleteSellerAccount = async () => {
      try {
        if (deleteSellerAccountId !== null) {
          const deleteSucceed = await sellerAccountService.deleteSellerAccount(deleteSellerAccountId);
          if (deleteSucceed === "Success") {
            getSellerAccounts();
          } else {
            console.error("Failed to delete seller account");
          }
        }
      } catch (err) {
        console.error("Failed to delete seller account", err);
      }
      finally {
        setDeleteSellerAccountId(null);
        setConfirmDeleteModalVisible(false);
      }
    }
    deleteSellerAccount();
  };

  const handleCancel = () => {
    setDeleteSellerAccountId(null);
    setConfirmDeleteModalVisible(false);
  };


  const handleLogoModelShown = () => {
    const uploadLogoFile = async () => {
      try {
        if (newLogoFile.current) {
          const serverFileName = await fileService.uploadSellerLogo(newLogoFile.current, (event: ProgressEvent) => {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            setLogoUploadProgress(percentCompleted);
          });
          setLogoUploadProgress(100);
          setSeller({ ...seller!, logo: serverFileName });
          setTimeout(() => {
            handleUploadLogoOnClose();
          }, 300);
        }
      } catch (err: any) {
        setLogoUploadErrorMessage("Failed to upload the logo.");
        setLogoUploadProgress(0);
      }
    }
    if (newLogoFile.current) {
      uploadLogoFile();
    }
  }

  const handleChangeButtonClick = () => {
    fileInputRef.current?.click();
  }

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSizeMB = 20;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      if (!allowedTypes.includes(file.type)) {
        newLogoFile.current = null;
        setLogoModalVisible(true);
        setLogoUploadErrorMessage('Invalid file format. Only JPG, PNG, and GIF are allowed.');
        return false;
      }
      if (file.size > maxSizeBytes) {
        newLogoFile.current = null;
        setLogoModalVisible(true);
        setLogoUploadErrorMessage(`File is too large. Maximum size is ${maxSizeMB} MB.`);
        return false;
      }
      newLogoFile.current = file;
      setLogoModalVisible(true);
    }
  }

  const handleUploadLogoOnClose = () => {
    setLogoModalVisible(false);
    setTimeout(() => {
      setLogoUploadProgress(0);
      setLogoUploadErrorMessage("");
    }, 200);
  }

  return (
    <div className='mt-3'>
      <div className="row">
        <div className="col-md-12">
          <div className="card rounded-0">
            <div className="card-body card-body p-4">
              <h5 className="card-title">Profile</h5>
              <div className="row">
                <div className="col-md-10 pt-3">
                  <label className="text-label mt-1">Store Name</label>
                  <p className="text-value mb-2">{seller?.sellerName}</p>
                  <label className="text-label">Description</label>
                  <p style={{ whiteSpace: "pre-line" }} className={`text-value mb-2 ${styles.textTruncateMultiline}`}>{seller?.sellerDescription || "-"}</p>
                  <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setProfileModalVisible(true)}>Edit</button>
                  <EditStoreProfileComp show={profileModalVisible} onClose={() => setProfileModalVisible(false)} />

                </div>
                <div className="col-md-2 pt-4">
                  <div className={`${styles.storeLogoView}`}>
                    <img className={`float-end ${styles.storeLogo}`} src={seller!.logo && seller!.logo !== "" ? `${sellerImageURL}/${seller!.logo}` : `${resourceURL}/images/default_logo.png`} alt="Store Logo" />
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleLogoChange} />
                    <button type="button" className={`btn btn-dark rounded-0 mt-2 float-end ${styles.changeStoreLogoButton}`} onClick={() => handleChangeButtonClick()}>{seller!.logo && seller!.logo !== "" ? "Change Logo" : "Upload Logo"}</button>
                    <UploadProgressModalComp title="Upload Logo" show={logoModalVisible} progress={logoUploadProgress} errorMessage={logoUploadErrorMessage} onClose={handleUploadLogoOnClose} onShown={() => handleLogoModelShown()} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12">
          <div className="card rounded-0 mt-3">
            <div className="card-body card-body p-4">
              <h5 className="card-title mb-3">Address</h5>
              <div className="row">
                <div className="col-md-4">
                  <label className="text-label mt-1">Address Line 1</label>
                  <p className="text-value mb-2 one-line-text-ellipsis">{seller!.addressLine1 || "-"}</p>
                </div>
                <div className="col-md-4">
                  <label className="text-label">Address Line 2</label>
                  <p className="text-value mb-2 one-line-text-ellipsis">{seller!.addressLine2 || "-"}</p>
                </div>
                <div className="col-4">
                  <label className="text-label">City</label>
                  <p className="text-value mb-2">{seller!.city || "-"}</p>
                </div>
              </div>
              <div className="row">
                <div className="col-4">
                  <label className="text-label">State / Province / Region</label>
                  <p className="text-value mb-2">{seller!.state || "-"}</p>
                </div>
                <div className="col-4">
                  <label className="text-label">ZIP / Postal Code</label>
                  <p className="text-value mb-2">{seller!.postcode || "-"}</p>
                </div>
                <div className="col-4">
                  <label className="text-label">Country</label>
                  <p className="text-value mb-2">{seller!.country || "-"}</p>
                </div>
              </div>
              <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setAddressModalVisible(true)}>Edit</button>
            </div>
          </div>
          <EditSellerAddressComp show={addressModalVisible} onClose={() => setAddressModalVisible(false)} />
        </div>
        <div className="col-md-12">
          <div className="card rounded-0 mt-3">
            <div className="card-body card-body p-4">
              <h5 className="card-title mb-3">Business &amp; Contact Details</h5>
              <div className="row">
                <div className="col-md-4">
                  <label className="text-label">Business Name</label>
                  <p className="text-value mb-2 one-line-text-ellipsis">{seller!.businessName || "-"}</p>
                </div>
                <div className="col-md-4">
                  <label className="text-label mt-1">Email</label>
                  <p className="text-value mb-2">{seller!.email || "-"}</p>
                </div>
                <div className="col-md-4">
                  <label className="text-label">Phone</label>
                  <p className="text-value mb-2">{`${seller!.countryCode} ${seller!.contactNumber || "-"}`}</p>
                </div>
              </div>
              <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setContactDetailsModalVisible(true)}>Edit</button>
            </div>
          </div>
          <EditSellerContactDetailsComp show={contactDetailsModalVisible} onClose={() => setContactDetailsModalVisible(false)} />
        </div>
        <div className="col-md-12">
          <div className="card rounded-0 mt-3">
            <div className="card-body card-body p-4">
              <h5 className="card-title mb-3">Payment Methods</h5>
              <label className="text-label mt-1">Payment Accepted</label>

              {seller?.paymentMethods && seller.paymentMethods.length > 0 ? (

                <div className="d-flex flex-row flex-wrap align-items-center py-2 gap-2 mb-3 mt-3">
                  {seller.paymentMethods.map((method) => (
                    <div
                      key={method.paymentMethodId}
                      className="d-flex flex-column align-items-center mb-2"
                    >
                      <img
                        src={`${resourceURL}/images/payment_methods/${method.iconImage}`}
                        alt={method.name}
                        className={`${styles.paymentMethodIcon} mb-1`}
                      />
                      <span className="text-value small">{method.name}</span>
                    </div>
                  ))}
                </div>

              ) : (
                <p className="text-value mb-2">No payment methods have been selected.</p>
              )}

              <label className="text-label">Payment Terms</label>
              <p className={`${styles.textTruncateMultiline} text-value mb-2 `} style={{ whiteSpace: "pre-line" }}>{seller!.paymentTerms || "-"}</p>
              <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setPaymentMethodsModalVisible(true)}>Edit</button>
            </div>
          </div>
          <EditPaymentMethodsComp show={paymentMethodsModalVisible} onClose={() => setPaymentMethodsModalVisible(false)} />
        </div>
        <div className={`col-md-12 ${sellerAccount?.accountRole !== "Owner" ? "mb-5" : ""}`}>
          <div className="card rounded-0 mt-3">
            <div className="card-body card-body p-4">
              <h5 className="card-title mb-3">Store Policy</h5>
              <label className="text-label mt-1">Shipping</label>
              <p className={`${styles.textTruncateMultiline} text-value mb-2 `} style={{ whiteSpace: "pre-line" }}> {seller!.shippingPolicy || "-"}</p>
              <label className="text-label">Return</label>
              <p className={`${styles.textTruncateMultiline} text-value mb-2 `} style={{ whiteSpace: "pre-line" }}>{seller!.returnPolicy || "-"}</p>
              <button type="button" className="btn btn-outline-dark rounded-0 mt-3" onClick={() => setStorePoliciesModalVisible(true)}>Edit</button>
            </div>
          </div>
          <EditStorePoliciesComp show={storePoliciesModalVisible} onClose={() => setStorePoliciesModalVisible(false)} />
        </div>
        {sellerAccount?.accountRole === "Owner" && (
          <>
            <div className="col-md-12 mb-5">
              <div className="card rounded-0 mt-3">
                <div className="card-body card-body p-4">
                  <h5 className="card-title mb-3">Staff Accounts</h5>
                  <button type="button" className="btn btn-outline-dark rounded-0 mb-2" onClick={() => addStaffAccount()}>Add New Account</button>
                  {sellerAccounts.length == 0 ? (
                    <div className="border fs-4 mt-4 p-5 text-center">
                      <i className="bi bi-stars clearfix"></i>
                      <span className='clearfix'>There are no staff accounts.</span>
                    </div>
                  ) : (
                    sellerAccounts.length > 0 && (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th scope="col">Email</th>
                            <th scope="col">First Name</th>
                            <th scope="col">Last Name</th>
                            <th scope="col" style={{ width: 73 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sellerAccounts.map((account) => (
                            <tr key={account.sellerAccountId}>
                              <td>{account.username} {account.accountRole === 'Owner' && <span className="badge bg-success text-small">Owner</span>}</td>
                              <td>{account.firstname}</td>
                              <td>{account.lastname}</td>
                              <td className="align-middle">{account.accountRole !== 'Owner' && (
                                <button className="btn  btn-sm btn-danger rounded-0" onClick={() => deleteStaffAccount(account.sellerAccountId)}>Delete</button>
                              )}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                </div>
              </div>
              <AddSellerAccountComp
                show={addSellerAccountModalVisible}
                onClose={() => setAddSellerAccountModalVisible(false)}
                onAdded={() => {
                  setAddSellerAccountModalVisible(false);
                  getSellerAccounts();
                }}
              />
            </div>
            <ConfirmDialogueComp
              show={confirmDeleteModalVisible}
              title="Confirm"
              message="Are you sure you want to delete this staff account?"
              onConfirm={handleDelete}
              onCancel={handleCancel}
            />
          </>
        )}

      </div>
    </div>
  );

};

export default StoreSettingsPage;
